import React from 'react'

import { makeStyles } from '@mui/styles'

import { inflect, titleize } from 'inflection'

import { Allowed } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import {
  DATE_FORMATS,
  formattedDate,
  getDateDiff,
  getDateTime,
} from '~/src/Lib/Utils'
import T from '~/src/UI/Shared/Typography'

const displayName = 'Harvest/Summary'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0, 1.5),
    display: 'flex',
    gap: theme.spacing(2.5),
  },
}), { name: displayName })

const HarvestSummary = ({ harvest, harvestPhases, onlyEssentials = false }) => {
  const formattedStartDate = formattedDate(harvest.startDate, DATE_FORMATS.SHORT_DATE)

  const firstDayOfFlower = harvestPhases.find(p => p.phaseType === 'FLOWER')?.startDate
  const formattedFlowerDate = formattedDate(firstDayOfFlower, DATE_FORMATS.SHORT_DATE)
  const flowerDateDiff = getDateDiff(firstDayOfFlower)
  const enteredFlower = flowerDateDiff.daysDiff > 0

  const formattedHarvestDate = formattedDate(harvest.harvestDate, DATE_FORMATS.SHORT_DATE)
  const harvestDateDiff = getDateDiff(harvest.harvestDate, getDateTime('now').startOf('day'))
  const harvested = Boolean(harvest.harvestDate) && harvestDateDiff.daysDiff > 0

  const classes = useStyles()

  const essentials = (
    <>
      <div>
        <T.Bold color="textPrimary">{formattedStartDate}</T.Bold>
        <T.Bold>Start</T.Bold>
      </div>

      {firstDayOfFlower && formattedFlowerDate !== formattedStartDate ? (
        <div>
          <T.Bold color="textPrimary">{formattedFlowerDate}</T.Bold>
          <T.Bold>{enteredFlower ? 'In Flower' : 'Est. Flower'}</T.Bold>
        </div>
      ) : null}

      <div>
        <T.Bold color="textPrimary">{harvest.harvestDate ? formattedHarvestDate : '--'}</T.Bold>
        <T.Bold>{harvested ? 'Harvested' : 'Est. Harvest'}</T.Bold>
      </div>
    </>
  )

  if (onlyEssentials) {
    return (
      <div className={classes.root}>
        {essentials}
      </div>
    )
  }

  const finishDateDiff = getDateDiff(harvest.endDate)
  const finished = finishDateDiff.daysDiff > 0

  const cultivarCount = harvest.cultivars.length
  const cultivarNames = harvest.cultivars.sort((a, b) => a.id - b.id).map(c => c.cultivarName).join(', ')

  return (
    <div className={classes.root}>
      {essentials}

      {harvest.harvestDate && !harvested ? (
        <div>
          <T.Bold color="textPrimary" noWrap>{titleize(harvestDateDiff.text)}</T.Bold>
          <T.Bold>Until Harvest</T.Bold>
        </div>
      ) : null}

      {harvested && !finished ? (
        <div>
          <T.Bold color="textPrimary" noWrap>{titleize(finishDateDiff.text)}</T.Bold>
          <T.Bold>Until Finished</T.Bold>
        </div>
      ) : null}

      {harvested && finished ? (
        <div>
          <T.Bold color="textPrimary" noWrap>{titleize(harvestDateDiff.text)}</T.Bold>
          <T.Bold>Since Harvest</T.Bold>
        </div>
      ) : null}

      <div>
        <T.Bold color="textPrimary">{harvest.plantCount || '--'}</T.Bold>
        <T.Bold>Plants</T.Bold>
      </div>

      <Allowed features="METRC">
        <div>
          <T.Bold color="textPrimary">{harvest.destroyedCount || '--'}</T.Bold>
          <T.Bold>Destroyed</T.Bold>
        </div>
      </Allowed>

      <div>
        <T.Bold color="textPrimary">{cultivarCount || '--'}</T.Bold>
        <T.Bold>{inflect('Cultivars', cultivarCount)} {cultivarCount ? `(${cultivarNames})` : ''}</T.Bold>
      </div>
    </div>
  )
}

const Memoized = React.memo(HarvestSummary)

export default Memoized
